import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import PanelBreadcrumbs from './PanelBreadcrumbs';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Dashboard = ({ setActivePage }) => {
  const [kpis, setKpis] = useState([]);
  const [revenueData, setRevenueData] = useState(null);
  const [ordersData, setOrdersData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchKPIData();
  }, []);

  const fetchKPIData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get auth token from localStorage
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;
      
      const response = await axios.get(`${API_BASE_URL}/api/kpi`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle the response data
      const data = response.data;
      
      // Update KPIs
      if (data.kpis && Array.isArray(data.kpis)) {
        setKpis(data.kpis);
      } else if (data.total_orders || data.total_revenue || data.active_products || data.total_merchants) {
        // Handle different response formats
        const kpiArray = [];
        if (data.total_orders !== undefined) {
          kpiArray.push({
            title: 'Total Orders',
            value: formatNumber(data.total_orders),
            change: data.orders_change || '+0%',
            trend: (data.orders_change && data.orders_change.startsWith('+')) ? 'up' : 'down',
            icon: '📦'
          });
        }
        if (data.total_revenue !== undefined) {
          kpiArray.push({
            title: 'Total Revenue',
            value: formatCurrency(data.total_revenue),
            change: data.revenue_change || '+0%',
            trend: (data.revenue_change && data.revenue_change.startsWith('+')) ? 'up' : 'down',
            icon: '💰'
          });
        }
        if (data.active_products !== undefined) {
          kpiArray.push({
            title: 'Active Products',
            value: formatNumber(data.active_products),
            change: data.products_change || '+0%',
            trend: (data.products_change && data.products_change.startsWith('+')) ? 'up' : 'down',
            icon: '📊'
          });
        }
        if (data.total_merchants !== undefined) {
          kpiArray.push({
            title: 'Total Merchants',
            value: formatNumber(data.total_merchants),
            change: data.merchants_change || '+0%',
            trend: (data.merchants_change && data.merchants_change.startsWith('+')) ? 'up' : 'down',
            icon: '🏪'
          });
        }
        setKpis(kpiArray);
      }

      // Update chart data if provided
      if (data.revenue_trend) {
        setRevenueData({
          labels: data.revenue_trend.labels || [],
          datasets: [{
            label: 'Revenue',
            data: data.revenue_trend.data || [],
            borderColor: 'rgba(99, 102, 241, 1)',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            tension: 0.4,
          }],
        });
      }

      if (data.orders_overview) {
        setOrdersData({
          labels: data.orders_overview.labels || [],
          datasets: [{
            label: 'Orders',
            data: data.orders_overview.data || [],
            backgroundColor: 'rgba(99, 102, 241, 0.7)',
          }],
        });
      }
    } catch (err) {
      // Don't show error if session expired (401) - user will be redirected to login
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      console.error('Error fetching KPI data:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (typeof num === 'number') {
      return num.toLocaleString('en-US');
    }
    return num || '0';
  };

  const formatCurrency = (num) => {
    if (typeof num === 'number') {
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return num || '$0';
  };

  // Default chart data if API doesn't provide it
  const defaultRevenueData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Revenue',
        data: [12000, 19000, 15000, 25000, 22000, 30000],
        borderColor: 'rgba(99, 102, 241, 1)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.4,
      },
    ],
  };

  const defaultOrdersData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Orders',
        data: [1200, 1900, 1500, 2500, 2200, 3000],
        backgroundColor: 'rgba(99, 102, 241, 0.7)',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-gray-600">Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-lg font-medium text-red-900">Error loading dashboard</h3>
              <p className="text-red-700 mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={fetchKPIData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <PanelBreadcrumbs current="Dashboard" setActivePage={setActivePage} />
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Welcome back! Here's what's happening with your business.</p>
      </div>

      {/* KPI Cards */}
      {kpis.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {kpis.map((kpi, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="text-3xl">{kpi.icon}</div>
                <span className={`text-sm font-medium ${
                  kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {kpi.change}
                </span>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">{kpi.title}</h3>
              <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Revenue Trend</h2>
          <div className="h-64">
            {revenueData ? (
              <Line data={revenueData} options={chartOptions} />
            ) : (
              <Line data={defaultRevenueData} options={chartOptions} />
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Orders Overview</h2>
          <div className="h-64">
            {ordersData ? (
              <Bar data={ordersData} options={chartOptions} />
            ) : (
              <Bar data={defaultOrdersData} options={chartOptions} />
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
        <div className="space-y-4">
          {[
            { action: 'New order received', details: 'Order #12345 - $299.99', time: '2 minutes ago' },
            { action: 'Product updated', details: 'iPhone 15 Pro - Stock updated', time: '15 minutes ago' },
            { action: 'New merchant registered', details: 'TechStore Inc.', time: '1 hour ago' },
            { action: 'Payment processed', details: '$1,234.56 - Order #12344', time: '2 hours ago' },
          ].map((activity, index) => (
            <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
              <div>
                <p className="font-medium text-gray-900">{activity.action}</p>
                <p className="text-sm text-gray-600">{activity.details}</p>
              </div>
              <span className="text-sm text-gray-500">{activity.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
